import { curry, findIndex, reverse } from "lodash/fp";

type ArrayUtilPredicate<T> = (item: T) => boolean;

/**
 * Replaces the index in the given list with the given item
 * If the index is outside the bounds of the array, the array will
 * be expanded (think of it as replacing an undefined value with the item)
 *
 * @returns a new list with the replaced item
 */
export const replaceAt = <T>(list: T[], item: T, index: number): T[] => {
  if (index < 0) return [...list];

  if (index > list.length - 1) {
    const newList = [...list];
    newList[index] = item;
    return newList;
  }

  return [...list.slice(0, index), item, ...list.slice(index + 1)];
};

/**
 * Returns a new array without the item at the provided index
 *
 * @returns a new list with the item removed
 */
export const removeAt = <T>(list: T[], index: number): T[] => [
  ...list.slice(0, index),
  ...list.slice(index + 1),
];

/**
 * Returns a new array without n (removeCount) items at the provided index
 *
 * @returns a new list with n items removed
 */
export const removeManyAt = <T>(
  list: T[],
  index: number,
  removeCount: number
): T[] => {
  if (removeCount <= 0) return [...list];

  return [...list.slice(0, index), ...list.slice(index + removeCount)];
};

/**
 * Returns a new list with the provided item at the given index
 * items after the provided index are shifted i + 1
 */
export const insertAt = <T>(list: T[], item: T, index: number): T[] => {
  if (index < 0) return [...list];

  if (index > list.length - 1) {
    const newList = [...list];
    newList[index] = item;
    return newList;
  }

  return [...list.slice(0, index), item, ...list.slice(index)];
};

/**
 * Returns a new list with the provided items at the given index
 *
 * @returns a new list with n items added
 */
export const insertManyAt = <T>(list: T[], items: T[], index: number): T[] => {
  if (index < 0) return [...list];

  if (index > list.length - 1) {
    return [...list, ...items];
  }

  return [...list.slice(0, index), ...items, ...list.slice(index)];
};

/**
 * Returns a new list with the provided item inserted after the first
 * item that satisfies the predicate. If none satisfy the predicate,
 * the item will not be inserted.
 */
export const insertAfter = <T>(
  list: T[],
  item: T,
  predicate: ArrayUtilPredicate<T>
): T[] => {
  const indexOfPredicateItem = findIndex(predicate, list);
  if (indexOfPredicateItem < 0) return [...list];
  return insertAt(list, item, indexOfPredicateItem + 1);
};

/**
 * Returns a new list with the provided item inserted after the last
 * item that satisfies the predicate. If none satisfy the predicate,
 * the item will not be inserted.
 */
export const insertAfterAll = <T>(
  list: T[],
  item: T,
  predicate: ArrayUtilPredicate<T>
): T[] => {
  const reversedList = reverse(list);
  const indexOfPredicateItem = findIndex(predicate, reversedList);
  return reverse(insertAt(reversedList, item, indexOfPredicateItem));
};

/**
 * Replaces the matching item in the list with the provided item.
 * Note: if more than one item in the list satisfies the predicate
 * condition, this method will currently only replace the first
 * matching item.
 *
 * @returns new list with the item replaced
 * */
export const replaceWhere = <T>(
  list: T[],
  item: T,
  predicate: ArrayUtilPredicate<T>
): T[] => {
  const index = findIndex(predicate, list);
  return replaceAt(list, item, index);
};

export const rotate = curry(<T>(count: number, list: T[]): T[] => {
  count -= list.length * Math.floor(count / list.length);
  return [...list.slice(count), ...list.slice(0, count)];
});

export const splitAt = <T>(list: T[], index: number): [T[], T[]] => {
  return [list.slice(0, index), list.slice(index)];
};
